/**
 * +valley: the Sunmeadow art kit (a painterly fantasy valley: dark teal conifers framing the edges,
 * grey-blue faceted cliffs with mossy tops, pale ancient ruins on the high ground, a glowing blue
 * crystal monument at the barrow, flagstone paths, bluebells and ferns).
 *
 * Same contract as outdoor/procs.ts (registered there, so ids resolve through `outdoorProc`):
 * non-indexed BufferGeometry in yards (position, normal, color), origin at the base centre, facing
 * +z, merged per chunk by the field builder. Faceted on purpose (flat normals): the toon ramp turns
 * each facet into a painted plane. Ids prefixed `glow_` go to the field's unlit layer (crystals),
 * `flagstone` to its flat ground layer (outdoor/layout.ts `layerOf`).
 */
import * as THREE from 'three';
import { type Gen, blob, combine, paint, registerOutdoorProcs, rockGeo, xf } from './procs';

type RGB3 = [number, number, number];

/** Valley palette (sRGB hex). */
export const VAL = {
  pineDark: 0x113234,
  pine: 0x1b4846,
  pineLight: 0x2c6150,
  pineTip: 0x4f8256,
  bark: 0x4a3a32,
  rockDark: 0x56637a,
  rock: 0x8592a6,
  rockLight: 0xaab6c4,
  moss: 0x56803a,
  mossLight: 0x6f9440,
  stone: 0xe8e3d6,
  stoneShade: 0xb9b6ae,
  stoneDark: 0x8f8e8c,
  ivy: 0x355f36,
  ivyLight: 0x4f8040,
  flag: 0xb4b8b2,
  flagWarm: 0xc6bea8,
  crystalDeep: 0x2a62d0,
  crystal: 0x5cb4ff,
  crystalLight: 0xc8f2ff,
  fern: 0x2f6a40,
  fernLight: 0x5f9a48,
  leaf: 0x3a7444,
} as const;

const tc = new THREE.Color();
const rgb = (hex: number): RGB3 => {
  tc.setHex(hex);
  return [tc.r, tc.g, tc.b];
};
const mix = (a: RGB3, b: RGB3, t: number): RGB3 => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
const clamp01 = (v: number): number => Math.min(1, Math.max(0, v));

/** Faceted copy (non-indexed, flat normals) with a per-vertex colour from `fn` (position + face normal). */
function shadeGeo(geo: THREE.BufferGeometry, fn: (x: number, y: number, z: number, nx: number, ny: number, nz: number) => RGB3): THREE.BufferGeometry {
  const g = geo.index ? geo.toNonIndexed() : geo;
  if (g !== geo) geo.dispose();
  g.deleteAttribute('uv');
  g.computeVertexNormals();
  const p = g.attributes['position'] as THREE.BufferAttribute;
  const n = g.attributes['normal'] as THREE.BufferAttribute;
  const col = new Float32Array(p.count * 3);
  for (let i = 0; i < p.count; i++) {
    const c = fn(p.getX(i), p.getY(i), p.getZ(i), n.getX(i), n.getY(i), n.getZ(i));
    col[i * 3] = c[0];
    col[i * 3 + 1] = c[1];
    col[i * 3 + 2] = c[2];
  }
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  return g;
}

/** Displace shared (coincident) vertices of a non-indexed geometry consistently. */
function displaceShared(g: THREE.BufferGeometry, fn: (v: THREE.Vector3) => void): void {
  const p = g.attributes['position'] as THREE.BufferAttribute;
  const seen = new Map<string, THREE.Vector3>();
  const v = new THREE.Vector3();
  for (let i = 0; i < p.count; i++) {
    const key = `${p.getX(i).toFixed(3)},${p.getY(i).toFixed(3)},${p.getZ(i).toFixed(3)}`;
    let out = seen.get(key);
    if (!out) {
      v.set(p.getX(i), p.getY(i), p.getZ(i));
      fn(v);
      out = v.clone();
      seen.set(key, out);
    }
    p.setXYZ(i, out.x, out.y, out.z);
  }
}

const box = (w: number, h: number, d: number): THREE.BufferGeometry => new THREE.BoxGeometry(w, h, d).translate(0, h / 2, 0);
const cyl = (rt: number, rb: number, h: number, seg = 8, hs = 1): THREE.BufferGeometry => new THREE.CylinderGeometry(rt, rb, h, seg, hs).translate(0, h / 2, 0);

// ---------------------------------------------------------------------------------------------
// Conifers: stacked tiers with jagged, drooping star skirts (stylised spruce / fir)
// ---------------------------------------------------------------------------------------------

/**
 * One tier: a cone whose rim alternates spikes (out, drooping) and notches (in, raised).
 * +detail: `soft` (0..1) rounds the tier: shallower notches, less droop and a full, convex side (a
 * mid ring pushed out), so stacked tiers read as soft layered boughs rather than spiky cones.
 */
function jaggedTier(r: number, h: number, points: number, rnd: () => number, soft = 0): THREE.BufferGeometry {
  const seg = points * 2;
  const g = new THREE.ConeGeometry(r, h, seg, soft > 0 ? 2 : 1).toNonIndexed();
  const step = (Math.PI * 2) / seg;
  const jit = Array.from({ length: seg }, () => 0.85 + rnd() * 0.3);
  displaceShared(g, (v) => {
    const rr = Math.hypot(v.x, v.z);
    if (rr < 1e-3) return;
    if (v.y > -h / 2 + 1e-3) {
      // +detail: the mid ring bulges (a rounded bough mass).
      if (soft > 0 && Math.abs(v.y) < 1e-3) {
        v.x *= 1 + 0.2 * soft;
        v.z *= 1 + 0.2 * soft;
        v.y += h * 0.04 * soft;
      }
      return;
    }
    const k = ((Math.round(Math.atan2(v.z, v.x) / step) % seg) + seg) % seg;
    const spike = k % 2 === 0;
    const f = (spike ? 1.0 : 0.6 + 0.22 * soft) * jit[k]!;
    v.x *= f;
    v.z *= f;
    v.y += spike ? -h * (0.2 + rnd() * 0.12) * (1 - 0.45 * soft) : h * 0.06 * (1 - 0.5 * soft);
  });
  return g;
}

function conifer(height: number, tiers: number, widthK: number, points = 7, soft = 0.8): Gen {
  return (rnd) => {
    const H = height * (0.92 + rnd() * 0.16);
    const parts: THREE.BufferGeometry[] = [paint(cyl(0.16, 0.3, H * 0.2, 6), VAL.bark, [0.5, 0.9])];
    const dark = rgb(VAL.pineDark);
    const mid = rgb(VAL.pine);
    const light = rgb(VAL.pineLight);
    const tip = rgb(VAL.pineTip);
    const lean = (rnd() - 0.5) * 0.06;
    for (let i = 0; i < tiers; i++) {
      const t = tiers > 1 ? i / (tiers - 1) : 0;
      const r = H * widthK * (1 - t * 0.8) * (0.9 + rnd() * 0.2);
      const h = H * (0.3 - t * 0.1);
      const y = H * 0.1 + t * H * 0.66;
      const tier = jaggedTier(r, h, points, rnd, soft);
      tier.rotateY(rnd() * Math.PI);
      tier.translate(lean * y, y + h / 2, 0);
      const ty0 = y - h * 0.4;
      parts.push(
        shadeGeo(tier, (_x, yy, _z, _nx, ny) => {
          // Darker under each skirt and toward the trunk; tops of the boughs catch light.
          const local = clamp01((yy - ty0) / (h * 1.2));
          const base = mix(mix(dark, mid, t * 0.8), light, local * 0.55);
          const up = clamp01(ny) * (0.25 + 0.35 * t);
          const c = mix(base, tip, up);
          const ao = 0.62 + 0.5 * local;
          return [c[0] * ao, c[1] * ao, c[2] * ao];
        }),
      );
    }
    return combine(parts);
  };
}

// ---------------------------------------------------------------------------------------------
// Cliffs and boulders: noise-displaced faceted rock, moss on the up-facing facets
// ---------------------------------------------------------------------------------------------

function cliffGeo(r: number, rnd: () => number, stretch: RGB3, detail: number, plateau: number, mossK: number): THREE.BufferGeometry {
  const g = new THREE.IcosahedronGeometry(r, detail);
  const o = [rnd() * 10, rnd() * 10, rnd() * 10];
  const top = r * stretch[1] * plateau;
  const bottom = -r * stretch[1] * 0.25;
  displaceShared(g, (v) => {
    const n = Math.sin(v.x * 1.7 / r + o[0]!) * Math.cos(v.z * 1.5 / r + o[1]!) * 0.5 + Math.sin(v.y * 2.3 / r + o[2]!) * 0.35;
    const k = 0.84 + 0.2 * n + (rnd() - 0.5) * 0.08;
    v.set(v.x * k * stretch[0], v.y * k * stretch[1], v.z * k * stretch[2]);
    if (v.y > top) v.y = top + (v.y - top) * 0.22;
    if (v.y < bottom) v.y = bottom;
  });
  g.translate(0, -bottom, 0);
  const H = top - bottom;
  const dark = rgb(VAL.rockDark);
  const mid = rgb(VAL.rock);
  const light = rgb(VAL.rockLight);
  const moss = rgb(VAL.moss);
  const mossL = rgb(VAL.mossLight);
  return shadeGeo(g, (x, y, z, _nx, ny) => {
    const t = clamp01(y / H);
    const facet = 0.94 + 0.12 * (Math.sin(x * 3.1 + z * 2.3 + o[0]!) * 0.5 + 0.5);
    const mossy = ny > 0.55 - 0.12 * Math.sin(x * 1.3 + z * 0.9 + o[1]!) - mossK * 0.2 && t > 0.25;
    if (mossy) {
      const c = mix(moss, mossL, clamp01((ny - 0.5) * 2) * 0.7 + t * 0.3);
      return [c[0] * facet, c[1] * facet, c[2] * facet];
    }
    const c = t < 0.5 ? mix(dark, mid, t * 2) : mix(mid, light, (t - 0.5) * 2);
    const ao = 0.78 + 0.28 * t;
    return [c[0] * ao * facet, c[1] * ao * facet, c[2] * ao * facet];
  });
}

const cliff = (r: number, stretch: RGB3, plateau: number, mossK = 0.5, detail = 2): Gen => (rnd) => cliffGeo(r, rnd, stretch, detail, plateau, mossK);

/** A small cluster: one big rock and a chip or two (edge lines, the barrow outcrop). */
const mossRock = (r: number): Gen => (rnd) => {
  const parts = [cliffGeo(r, rnd, [1.15, 0.8, 1.0], 1, 0.7, 0.6)];
  const n = 1 + Math.floor(rnd() * 2);
  for (let i = 0; i < n; i++) {
    const a = rnd() * Math.PI * 2;
    parts.push(cliffGeo(r * (0.35 + rnd() * 0.2), rnd, [1.1, 0.8, 1.0], 1, 0.7, 0.4).translate(Math.cos(a) * r * 1.1, 0, Math.sin(a) * r * 1.1));
  }
  return combine(parts);
};

// ---------------------------------------------------------------------------------------------
// Pale ruins
// ---------------------------------------------------------------------------------------------

const stoneShade = (lo = 0.72, hi = 1.04) => {
  const s = rgb(VAL.stone);
  const d = rgb(VAL.stoneShade);
  return (y0: number, y1: number) =>
    (x: number, y: number, z: number, _nx: number, ny: number): RGB3 => {
      const t = clamp01((y - y0) / Math.max(0.01, y1 - y0));
      const c = mix(d, s, t * 0.8 + clamp01(ny) * 0.2);
      const k = (lo + (hi - lo) * t) * (0.95 + 0.08 * Math.sin(x * 4.1 + z * 3.3 + y * 2.2));
      return [c[0] * k, c[1] * k, c[2] * k];
    };
};
const pale = stoneShade();

/** Moss cushion (flattened lumpy blob). */
function mossCap(r: number, rnd: () => number, at: RGB3, squash = 0.32): THREE.BufferGeometry {
  return paint(xf(blob(r, rnd, 0.3, 7, 5, 0.5), at, [0, rnd() * 3, 0], [1, squash, 1]), VAL.moss, [0.85, 1.15]);
}

/** Ivy hanging down a face: a strip with a few leaf clumps (local +z = out of the face). */
function ivy(len: number, rnd: () => number, at: RGB3, yaw: number): THREE.BufferGeometry[] {
  const parts: THREE.BufferGeometry[] = [];
  const strip = paint(box(0.22 + rnd() * 0.12, len, 0.08), VAL.ivy, [0.7, 1.05]);
  parts.push(xf(strip, [at[0], at[1] - len, at[2]], [0, yaw, (rnd() - 0.5) * 0.12]));
  const n = 2 + Math.floor(len / 0.9);
  for (let i = 0; i < n; i++) {
    const y = at[1] - (i / n) * len - 0.2;
    parts.push(paint(xf(blob(0.2 + rnd() * 0.14, rnd, 0.3, 6, 4, 0), [at[0] + (rnd() - 0.5) * 0.4, y, at[2]], [0, 0, 0], [1, 0.8, 0.6]), i % 2 ? VAL.ivy : VAL.ivyLight, [0.8, 1.1]));
  }
  return parts;
}

/** A broken column on a plinth: fluted-ish shaft, jagged top, moss and ivy. */
function columnGeo(rnd: () => number, h: number, intact: boolean, r = 0.52): THREE.BufferGeometry[] {
  const parts: THREE.BufferGeometry[] = [];
  parts.push(shadeGeo(box(1.45, 0.45, 1.45), pale(0, 0.45)));
  parts.push(shadeGeo(xf(box(1.2, 0.2, 1.2), [0, 0.45, 0]), pale(0.4, 0.7)));
  const shaft = new THREE.CylinderGeometry(r * 0.92, r, h, 10, 3).toNonIndexed();
  if (!intact) displaceShared(shaft, (v) => (v.y > h / 2 - 0.01 ? (v.y -= rnd() * 0.7) : undefined));
  shaft.translate(0, 0.65 + h / 2, 0);
  parts.push(shadeGeo(shaft, pale(0.6, 0.65 + h)));
  if (intact) {
    parts.push(shadeGeo(xf(box(1.3, 0.22, 1.3), [0, 0.65 + h, 0]), pale(h, h + 1)));
    parts.push(shadeGeo(xf(box(1.5, 0.35, 1.5), [0, 0.87 + h, 0]), pale(h, h + 1.2)));
    parts.push(mossCap(0.62, rnd, [0.1, 1.22 + h, 0]));
  } else parts.push(mossCap(0.5, rnd, [0, 0.4 + h, 0], 0.4));
  parts.push(mossCap(0.8, rnd, [0.2, 0.15, 0.25], 0.25));
  if (rnd() < 0.75) parts.push(...ivy(h * (0.4 + rnd() * 0.35), rnd, [Math.sin(0.6) * r * 1.02, 0.6 + h * 0.95, Math.cos(0.6) * r * 1.02], 0.6));
  return parts;
}

const columnPale: Gen = (rnd) => combine(columnGeo(rnd, 2.4 + rnd() * 2.4, rnd() < 0.3));

/** Rubble: a few pale blocks half sunk in the grass. */
function rubble(rnd: () => number, n: number, spread: number, at: RGB3 = [0, 0, 0]): THREE.BufferGeometry[] {
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < n; i++) {
    const a = rnd() * Math.PI * 2;
    const d = spread * (0.4 + rnd() * 0.6);
    const s = 0.35 + rnd() * 0.35;
    parts.push(shadeGeo(xf(box(s * 1.4, s, s), [at[0] + Math.cos(a) * d, at[1] - s * 0.25, at[2] + Math.sin(a) * d], [rnd() * 0.3, rnd() * 3, rnd() * 0.3]), pale(-0.2, 0.6)));
  }
  return parts;
}

/** A pale arch: two square piers and a round arch of voussoirs (sometimes broken), moss, ivy. */
function archGeo(rnd: () => number, span: number, pierH: number, broken: boolean): THREE.BufferGeometry[] {
  const parts: THREE.BufferGeometry[] = [];
  const R = span / 2;
  for (const sx of [-1, 1]) {
    const h = broken && sx > 0 ? pierH * (0.55 + rnd() * 0.2) : pierH;
    parts.push(shadeGeo(xf(box(1.5, 0.5, 1.5), [sx * R, 0, 0]), pale(0, 0.5)));
    parts.push(shadeGeo(xf(box(1.15, h, 1.15), [sx * R, 0.5, 0]), pale(0.3, h + 0.5)));
    parts.push(shadeGeo(xf(box(1.35, 0.3, 1.35), [sx * R, 0.5 + h, 0]), pale(h, h + 1)));
    if (h < pierH) parts.push(mossCap(0.6, rnd, [sx * R, 0.8 + h, 0], 0.45));
  }
  const n = 9;
  const last = broken ? 5 + Math.floor(rnd() * 2) : n;
  for (let i = 0; i < last; i++) {
    const a = Math.PI - ((i + 0.5) / n) * Math.PI;
    const v = new THREE.BoxGeometry(((Math.PI * R) / n) * 1.04, 0.95, 1.1);
    v.rotateZ(a - Math.PI / 2);
    v.translate(Math.cos(a) * (R + 0.05), pierH + 0.8 + Math.sin(a) * (R + 0.05), 0);
    parts.push(shadeGeo(v, pale(pierH, pierH + R + 1.5)));
  }
  // Moss on the crown and a drape of ivy from the arch.
  const crownA = broken ? Math.PI - ((last - 1) / n) * Math.PI : Math.PI / 2;
  parts.push(mossCap(0.75, rnd, [Math.cos(crownA) * R, pierH + 1.25 + Math.sin(crownA) * R, 0], 0.4));
  parts.push(mossCap(0.6, rnd, [-R * 0.75, pierH + 1.1 + R * 0.6, 0.1], 0.35));
  parts.push(...ivy(1.6 + rnd() * 1.4, rnd, [-R * 0.55, pierH + 0.7 + R * 0.8, 0.58], 0));
  parts.push(...ivy(1.2 + rnd() * 1.6, rnd, [-R - 0.6, pierH + 0.4, 0.2], -Math.PI / 2));
  return parts;
}

const ruinArch: Gen = (rnd) => combine([...archGeo(rnd, 5.2, 4.6 + rnd() * 1.2, rnd() < 0.5), ...rubble(rnd, 3, 3.2)]);

/** A row of columns on a stylobate, an architrave over the intact ones (~10 yd along x). */
const ruinColonnade: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [shadeGeo(box(11, 0.5, 2.2), pale(0, 0.5))];
  const H = 4.6;
  const intact: boolean[] = [];
  for (let i = 0; i < 4; i++) {
    const keep = i < 2 || (i === 2 && rnd() < 0.5);
    intact.push(keep);
    const cols = columnGeo(rnd, keep ? H : 1.4 + rnd() * 2.2, keep, 0.48);
    for (const c of cols) parts.push(c.translate(-3.9 + i * 2.6, 0.5, 0));
  }
  const span = intact.filter(Boolean).length;
  if (span >= 2) {
    const w = (span - 1) * 2.6 + 1.6;
    const x = -3.9 + ((span - 1) * 2.6) / 2;
    parts.push(shadeGeo(xf(box(w, 0.7, 1.5), [x, 0.5 + 0.65 + H + 0.57, 0]), pale(H, H + 3)));
    parts.push(mossCap(0.9, rnd, [x - 0.6, 0.5 + 0.65 + H + 1.3, 0], 0.35));
    parts.push(...ivy(2.2, rnd, [x + 0.8, 0.5 + 0.65 + H + 1.0, 0.78], 0));
  }
  parts.push(...rubble(rnd, 4, 3.5, [3, 0.5, 1.6]));
  return combine(parts);
};

/** A broken wall stretch (~6 yd along x): stacked blocks with a ragged top. */
const ruinWallPale: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 6; i++) {
    const h = 1.0 + rnd() * 2.4 * (i === 2 || i === 3 ? 1.3 : 0.75);
    parts.push(shadeGeo(xf(box(1.05, h, 0.9), [-2.6 + i * 1.04, 0, (rnd() - 0.5) * 0.12], [0, (rnd() - 0.5) * 0.06, 0]), pale(0, h)));
    if (rnd() < 0.5) parts.push(mossCap(0.45, rnd, [-2.6 + i * 1.04, h + 0.05, 0], 0.4));
  }
  parts.push(...ivy(1.8, rnd, [-0.5, 2.6, 0.48], 0));
  parts.push(...rubble(rnd, 3, 2.4, [0, 0, 1.2]));
  return combine(parts);
};

/** A ruined watchtower on the high ground (~15 yd): round shaft, dark slits, broken crown. */
const ruinTower: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  const H = 13 + rnd() * 3;
  parts.push(shadeGeo(cyl(3.6, 3.9, 1.2, 10), pale(0, 1.2)));
  const shaft = new THREE.CylinderGeometry(2.5, 3.0, H, 10, 4).toNonIndexed();
  displaceShared(shaft, (v) => {
    if (v.y > H / 2 - 0.01) {
      const a = Math.atan2(v.z, v.x);
      v.y -= (Math.sin(a * 3 + 1) * 0.5 + 0.5) * 3.5 + rnd() * 0.8;
    }
  });
  shaft.translate(0, 1.2 + H / 2, 0);
  parts.push(shadeGeo(shaft, pale(0, H + 1.2)));
  for (const y of [5.5, 10.5]) parts.push(shadeGeo(xf(new THREE.CylinderGeometry(2.95 - y * 0.035, 3.0 - y * 0.035, 0.45, 10), [0, y, 0]), pale(y - 1, y + 1)));
  for (let i = 0; i < 5; i++) {
    const a = i * 2.2 + rnd();
    const y = 3 + i * 2.3;
    const r = 3.0 - (y - 1.2) * (0.5 / H) + 0.02;
    parts.push(paint(xf(box(0.55, 1.5, 0.12), [Math.cos(a) * r, y, Math.sin(a) * r], [0, Math.PI / 2 - a, 0]), 0x26303c, [1, 1]));
  }
  parts.push(mossCap(2.2, rnd, [0.4, H - 1.6, 0.2], 0.3));
  parts.push(mossCap(1.4, rnd, [-1.6, 1.4, 2.2], 0.35));
  parts.push(...ivy(5 + rnd() * 3, rnd, [Math.cos(0.4) * 2.7, H - 1.0, Math.sin(0.4) * 2.7], Math.PI / 2 - 0.4));
  parts.push(...ivy(3 + rnd() * 3, rnd, [Math.cos(2.6) * 2.7, H - 3.0, Math.sin(2.6) * 2.7], Math.PI / 2 - 2.6));
  parts.push(...rubble(rnd, 5, 5));
  return combine(parts);
};

// ---------------------------------------------------------------------------------------------
// Crystals (the unlit `glow_` layer: lighting is baked into the vertex colours)
// ---------------------------------------------------------------------------------------------

const SUN = new THREE.Vector3(-0.45, 0.75, 0.5).normalize();

/** A hexagonal crystal (prism + point) along +y from the origin. */
function crystalGeo(r: number, h: number): THREE.BufferGeometry {
  const body = new THREE.CylinderGeometry(r * 0.85, r, h * 0.72, 6).translate(0, h * 0.36, 0);
  const tip = new THREE.ConeGeometry(r * 0.85, h * 0.28, 6).translate(0, h * 0.86, 0);
  return combine([body.toNonIndexed(), tip.toNonIndexed()].map((g) => (g.deleteAttribute('uv'), g)));
}

function crystalShade(h0: number, h1: number, boost: number) {
  const deep = rgb(VAL.crystalDeep);
  const mid = rgb(VAL.crystal);
  const light = rgb(VAL.crystalLight);
  return (_x: number, y: number, _z: number, nx: number, ny: number, nz: number): RGB3 => {
    const t = clamp01((y - h0) / Math.max(0.01, h1 - h0));
    const lit = clamp01(nx * SUN.x + ny * SUN.y + nz * SUN.z);
    const base = t < 0.55 ? mix(deep, mid, t / 0.55) : mix(mid, light, (t - 0.55) / 0.45);
    const c = mix(base, light, lit * lit * 0.6);
    const k = (0.75 + 0.45 * lit) * boost;
    return [c[0] * k, c[1] * k, c[2] * k];
  };
}

/** Place a crystal (tilted, turned) and bake its shading in the final pose. */
function crystalAt(r: number, h: number, at: RGB3, tilt: RGB3, boost = 1.15): THREE.BufferGeometry {
  const g = xf(crystalGeo(r, h), at, tilt);
  g.computeBoundingBox();
  return shadeGeo(g, crystalShade(g.boundingBox!.min.y, g.boundingBox!.max.y, boost));
}

/** The crystal monument: one tall shard and a ring of smaller ones (unlit, glows via the bloom). */
const glowMonument: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [crystalAt(0.95, 7.2, [0, -0.4, 0], [0.05, rnd(), -0.06], 1.25)];
  const n = 6;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + rnd() * 0.5;
    const h = 2.2 + rnd() * 2.6;
    const tilt = 0.25 + rnd() * 0.35;
    parts.push(crystalAt(0.4 + rnd() * 0.25, h, [Math.cos(a) * 0.85, -0.3, Math.sin(a) * 0.85], [Math.sin(a) * tilt, rnd() * 3, -Math.cos(a) * tilt], 1.05));
  }
  return combine(parts);
};

/** A small crystal cluster (unlit). */
const glowCrystals: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  const n = 3 + Math.floor(rnd() * 3);
  for (let i = 0; i < n; i++) {
    const a = rnd() * Math.PI * 2;
    const h = 0.9 + rnd() * 1.3;
    const tilt = 0.15 + rnd() * 0.45;
    parts.push(crystalAt(0.2 + rnd() * 0.08, h, [Math.cos(a) * 0.25, -0.1, Math.sin(a) * 0.25], [Math.sin(a) * tilt, rnd() * 3, -Math.cos(a) * tilt], 1.05));
  }
  return combine(parts);
};

// ---------------------------------------------------------------------------------------------
// The barrow as a ruin: a mossy rock outcrop, a pale arch over the mouth (facing +z), crystals.
// Same footprint as procs.ts barrowMound (the dungeon portal's swirl sits at local z ≈ 1.1).
// ---------------------------------------------------------------------------------------------

const barrowRuin: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  // Outcrop: a big shelf behind, two shoulders either side of the arch.
  parts.push(xf(cliffGeo(4.2, rnd, [1.25, 1.0, 1.0], 2, 0.72, 0.7), [0, -0.2, -4.6]));
  parts.push(xf(cliffGeo(2.6, rnd, [1.0, 1.15, 1.0], 2, 0.7, 0.6), [-4.1, -0.2, -1.4]));
  parts.push(xf(cliffGeo(2.4, rnd, [1.0, 1.1, 1.0], 2, 0.7, 0.6), [4.0, -0.2, -1.8]));
  // Dark recess behind the swirl.
  parts.push(paint(xf(box(3.6, 4.3, 1.6), [0, -0.3, -0.1]), 0x0b1218, [1, 1]));
  // The arch over the mouth (piers either side of the swirl).
  for (const p of archGeo(rnd, 4.7, 4.0, false)) parts.push(p.translate(0, 0, 1.6));
  // Threshold slab and two leaning broken columns flanking the steps.
  parts.push(shadeGeo(xf(box(4.2, 0.12, 2.2), [0, -0.04, 3.0]), pale(-0.1, 0.1)));
  for (const sx of [-1, 1]) for (const c of columnGeo(rnd, 1.6 + rnd() * 1.2, false, 0.42)) parts.push(xf(c, [sx * 3.8, 0, 2.6], [0, 0, sx * 0.05]));
  // Rune band on the arch's front (bright albedo; the portal's glow card sits on it).
  parts.push(paint(xf(box(2.6, 0.18, 0.05), [0, 4.55, 2.19]), 0x9fe6ff, [1.15, 1.25]));
  return combine(parts);
};

// ---------------------------------------------------------------------------------------------
// Ground cover: flagstones, bluebells, ferns, teal shrubs
// ---------------------------------------------------------------------------------------------

/** One irregular flat stone (~1 yd across), top at ≈ 0.07. */
const flagstone: Gen = (rnd) => {
  const n = 6 + Math.floor(rnd() * 3);
  const pts: THREE.Vector2[] = [];
  const sx = 0.85 + rnd() * 0.35;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + (rnd() - 0.5) * 0.5;
    const r = 0.5 * (0.72 + rnd() * 0.32);
    pts.push(new THREE.Vector2(Math.cos(a) * r * sx, Math.sin(a) * r));
  }
  const shape = new THREE.Shape(pts);
  const g = new THREE.ExtrudeGeometry(shape, { depth: 0.12, bevelEnabled: true, bevelThickness: 0.03, bevelSize: 0.04, bevelSegments: 1 });
  g.rotateX(-Math.PI / 2).translate(0, -0.08, 0);
  const warm = rnd();
  const base = mix(rgb(VAL.flag), rgb(VAL.flagWarm), warm);
  const dark = rgb(VAL.stoneDark);
  return shadeGeo(g, (x, _y, z, _nx, ny) => {
    const k = ny > 0.8 ? 0.95 + 0.1 * Math.sin(x * 5 + z * 4 + warm * 9) : 0.7;
    const c = ny > 0.8 ? base : mix(base, dark, 0.5);
    return [c[0] * k, c[1] * k, c[2] * k];
  });
};

/** Bluebells / violets: leafy tufts with small bell heads. */
function bells(colors: readonly number[], leaf: number): Gen {
  return (rnd) => {
    const parts: THREE.BufferGeometry[] = [];
    const n = 8 + Math.floor(rnd() * 6);
    for (let i = 0; i < n; i++) {
      const a = rnd() * Math.PI * 2;
      const d = Math.sqrt(rnd()) * 0.7;
      const x = Math.cos(a) * d;
      const z = Math.sin(a) * d;
      const h = 0.26 + rnd() * 0.24;
      // +detail: low-poly (open 3-sided stems, 5-sided bells): ground cover is everywhere now.
      parts.push(paint(xf(new THREE.CylinderGeometry(0.014, 0.02, h, 3, 1, true).translate(0, h / 2, 0), [x, 0, z], [(rnd() - 0.5) * 0.3, 0, (rnd() - 0.5) * 0.3]), 0x2f5f2c, [0.8, 1]));
      const c = colors[i % colors.length]!;
      parts.push(paint(xf(new THREE.ConeGeometry(0.07 + rnd() * 0.03, 0.13, 5).rotateX(Math.PI), [x, h - 0.02, z], [rnd() * 0.5, rnd() * 3, 0]), c, [0.85, 1.15]));
    }
    for (let i = 0; i < 2; i++) {
      const a = rnd() * Math.PI * 2;
      parts.push(paint(xf(blob(0.24, rnd, 0.3, 5, 3, 0.4), [Math.cos(a) * 0.3, 0.05, Math.sin(a) * 0.3], [0, 0, 0], [1.2, 0.45, 0.8]), leaf, [0.7, 1.05]));
    }
    return combine(parts);
  };
}

/** +zone: a slime puddle: a glossy green smear on the ground with a few bubbles (the Slime Hollows). */
const slimePuddle: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [paint(xf(blob(0.9, rnd, 0.35, 10, 4, 0.5), [0, 0.0, 0], [0, 0, 0], [1.3, 0.08, 1.0]), 0x6fd85a, [1.0, 1.25])];
  for (let i = 0; i < 4; i++) {
    const a = rnd() * Math.PI * 2;
    const d = rnd() * 0.7;
    parts.push(paint(xf(new THREE.SphereGeometry(0.08 + rnd() * 0.08, 6, 4), [Math.cos(a) * d, 0.06, Math.sin(a) * d]), 0x9cf07a, [1.1, 1.3]));
  }
  return combine(parts);
};

/** +zone: a tuft of barley: pale gold stalks with heavy heads (the Boar Run's fields). */
const barley: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  const n = 10 + Math.floor(rnd() * 8);
  for (let i = 0; i < n; i++) {
    const a = rnd() * Math.PI * 2;
    const d = Math.sqrt(rnd()) * 0.6;
    const x = Math.cos(a) * d;
    const z = Math.sin(a) * d;
    const h = 0.55 + rnd() * 0.35;
    const lean: [number, number, number] = [(rnd() - 0.5) * 0.35, 0, (rnd() - 0.5) * 0.35];
    parts.push(paint(xf(cyl(0.012, 0.018, h, 4), [x, 0, z], lean), 0xb8a85a, [0.85, 1.05]));
    parts.push(paint(xf(new THREE.ConeGeometry(0.045, 0.2, 5).rotateX(Math.PI), [x + lean[0] * h * 0.9, h + 0.06, z + lean[2] * h * 0.9], lean), 0xe2c872, [0.95, 1.2]));
  }
  return combine(parts);
};

/** A fern: 6–8 arching tapered fronds with a few leaflet teeth. */
const fern: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  const n = 6 + Math.floor(rnd() * 3);
  const dark = rgb(VAL.fern);
  const light = rgb(VAL.fernLight);
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + rnd() * 0.4;
    const L = 0.75 + rnd() * 0.35;
    // Frond: a flat tapered blade (two faces) bent down along its length.
    const g = new THREE.PlaneGeometry(0.34, L, 1, 4).toNonIndexed();
    displaceShared(g, (v) => {
      const t = (v.y + L / 2) / L;
      v.x *= 1 - t * 0.85;
      // Saw-tooth edge.
      v.x *= 1 + 0.25 * Math.sin(t * 22);
      const along = t * L;
      v.set(v.x, Math.sin(t * 2.2) * L * 0.45, along * 0.9 - t * t * 0.1);
    });
    const back = g.clone().scale(-1, 1, 1);
    for (const f of [g, back]) {
      f.translate(0, 0.02, 0.05).rotateY(a);
      parts.push(shadeGeo(f, (x, _y, z) => mix(dark, light, clamp01(Math.hypot(x, z) / L))));
    }
  }
  return combine(parts);
};

/** Low teal shrub (replaces the round lime bush in the valley). */
const shrub: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  const n = 3 + Math.floor(rnd() * 2);
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + rnd();
    const r = 0.5 + rnd() * 0.25;
    parts.push(paint(xf(blob(r, rnd, 0.28, 7, 5, 0.3), [Math.cos(a) * 0.42, r * 0.55, Math.sin(a) * 0.42]), i % 2 ? VAL.pine : VAL.leaf, [0.55, 1.1], [0, 1.2]));
  }
  parts.push(paint(xf(blob(0.55, rnd, 0.25, 7, 5, 0.3), [0, 0.75, 0]), VAL.pineLight, [0.8, 1.15], [0, 1.3]));
  return combine(parts);
};

/** Pale stone portal ring (the Bramblewood portal in the valley). */
const portalRingPale: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  const n = 11;
  for (let i = 0; i < n; i++) {
    const a = (i / (n - 1)) * Math.PI;
    const s = 0.55 + rnd() * 0.15;
    parts.push(shadeGeo(xf(rockGeo(s, rnd, 1.0), [Math.cos(a) * 2.3, Math.sin(a) * 2.9 - s * 0.4, 0], [0, 0, a - Math.PI / 2], [0.8, 1.2, 0.7]), pale(-0.5, 3.2)));
  }
  parts.push(shadeGeo(box(5.6, 0.25, 1.6), pale(0, 0.3)));
  parts.push(mossCap(0.7, rnd, [-1.4, 2.7, 0], 0.4));
  for (const sx of [-1, 1]) parts.push(xf(cliffGeo(0.7, rnd, [1, 0.9, 1], 1, 0.7, 0.6), [sx * 3.0, 0, 0.4]));
  return combine(parts);
};

// ---------------------------------------------------------------------------------------------
// +zone: the lake, the mine, the grotto, lore stones
// ---------------------------------------------------------------------------------------------

/** A clump of reeds and cattails (lake shores; walk-through detail). */
const reeds: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  const n = 7 + Math.floor(rnd() * 5);
  for (let i = 0; i < n; i++) {
    const a = rnd() * Math.PI * 2;
    const d = Math.sqrt(rnd()) * 0.45;
    const h = 0.9 + rnd() * 0.8;
    const tilt: RGB3 = [(rnd() - 0.5) * 0.25, 0, (rnd() - 0.5) * 0.25];
    parts.push(paint(xf(new THREE.ConeGeometry(0.035, h, 4).translate(0, h / 2, 0), [Math.cos(a) * d, 0, Math.sin(a) * d], tilt), i % 3 ? 0x5f8a3a : 0x7f9a46, [0.7, 1.1]));
    if (i % 3 === 0) parts.push(paint(xf(cyl(0.045, 0.045, 0.22, 5), [Math.cos(a) * d + tilt[2] * -h * 0.8, h * 0.78, Math.sin(a) * d + tilt[0] * h * 0.8]), 0x6a4a30, [0.9, 1]));
  }
  return combine(parts);
};

/** A short fishing dock: planks on posts, running out along +z. */
const dock: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  const L = 8;
  for (let i = 0; i < 10; i++) parts.push(paint(xf(box(2.2, 0.1, 0.72), [0, 0.35, 0.4 + i * 0.8], [0, (rnd() - 0.5) * 0.04, (rnd() - 0.5) * 0.03]), i % 2 ? 0x8a6a46 : 0x9c7a50, [0.85, 1.05]));
  for (const sx of [-1, 1]) for (let k = 0; k <= 3; k++) parts.push(paint(xf(cyl(0.11, 0.13, 1.6, 6), [sx * 1.0, -1.2, (k / 3) * L]), 0x5a3f2a, [0.6, 1]));
  parts.push(paint(xf(box(0.4, 0.5, 0.4), [0.7, 0.45, L - 0.6]), 0x7a5a38, [0.8, 1]));
  return combine(parts);
};

/** The mine's adit: a timber frame set into a tall rock face, a dark tunnel, rails running out (+z). */
const mineMouth: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  parts.push(xf(cliffGeo(4.2, rnd, [1.9, 2.2, 1.1], 2, 0.8, 0.25), [0, -0.5, -3.4]));
  for (const sx of [-1, 1]) parts.push(xf(cliffGeo(2.8, rnd, [1.0, 2.0, 1.1], 2, 0.8, 0.3), [sx * 5.2, -0.4, -1.4]));
  // Support beams inside the mouth, receding.
  for (let i = 1; i <= 2; i++) {
    for (const sx of [-1, 1]) parts.push(paint(xf(box(0.3, 3.2, 0.3), [sx * 1.4, 0, -i * 1.1]), 0x3f2c1e, [0.5, 0.8]));
    parts.push(paint(xf(box(3.2, 0.35, 0.35), [0, 3.1, -i * 1.1]), 0x3f2c1e, [0.6, 0.8]));
  }
  parts.push(paint(xf(box(3.0, 3.4, 1.4), [0, 0, -0.2]), 0x0a0e12, [1, 1]));
  for (const sx of [-1, 1]) parts.push(paint(xf(box(0.4, 3.6, 0.4), [sx * 1.7, 0, 0.6], [0, 0, sx * -0.04]), 0x5a3f2a, [0.6, 1]));
  parts.push(paint(xf(box(4.2, 0.45, 0.5), [0, 3.5, 0.6]), 0x6a4a30, [0.8, 1]));
  for (const sx of [-0.5, 0.5]) parts.push(paint(xf(box(0.08, 0.06, 7), [sx, 0.03, 3.6]), 0x5a5a62, [1, 1]));
  for (let i = 0; i < 9; i++) parts.push(paint(xf(box(1.5, 0.06, 0.18), [0, 0.0, 0.4 + i * 0.8]), 0x5a3f2a, [0.8, 1]));
  parts.push(paint(xf(box(0.22, 0.3, 0.22), [1.2, 2.6, 0.9]), 0xffd68a, [1.3, 1.4]));
  return combine(parts);
};

/** A grotto entrance: a rough rock arch (+z is outward). */
const caveMouth: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  for (const sx of [-1, 1]) parts.push(xf(cliffGeo(1.8, rnd, [0.9, 1.9, 1.1], 1, 0.8, 0.5), [sx * 3.0, -0.2, 0]));
  parts.push(xf(cliffGeo(2.0, rnd, [2.2, 0.7, 1.1], 1, 0.7, 0.9), [0, 4.2, 0]));
  return combine(parts);
};

/** A lore stone: a pale tablet with a glowing rune, moss at its foot. */
const loreStone: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [shadeGeo(box(1.6, 0.35, 1.0), pale(0, 0.35))];
  const g = new THREE.BoxGeometry(1.2, 2.2, 0.35, 1, 2, 1).toNonIndexed();
  displaceShared(g, (v) => (v.y > 1.0 ? (v.x *= 0.82) : undefined));
  parts.push(shadeGeo(g.translate(0, 1.45, 0), pale(0.3, 2.6)));
  parts.push(paint(xf(new THREE.TorusGeometry(0.32, 0.05, 4, 16), [0, 1.7, 0.19]), 0x9fe6ff, [1.3, 1.4]));
  parts.push(mossCap(0.7, rnd, [0.2, 0.25, 0.2], 0.3));
  return combine(parts);
};

/**
 * +zone: a waystone: a tall pale obelisk on a round dais ringed by short stones, a glowing rune band
 * and a crystal cap (fast travel between attuned stones).
 */
const waystone: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [shadeGeo(new THREE.CylinderGeometry(2.1, 2.4, 0.4, 14).translate(0, 0.2, 0), pale(0, 0.4))];
  parts.push(shadeGeo(new THREE.CylinderGeometry(1.3, 1.5, 0.3, 10).translate(0, 0.55, 0), pale(0.3, 0.7)));
  const g = new THREE.CylinderGeometry(0.42, 0.62, 3.8, 4, 3).toNonIndexed();
  displaceShared(g, (v) => {
    v.x += (rnd() - 0.5) * 0.04;
  });
  parts.push(shadeGeo(g.rotateY(Math.PI / 4).translate(0, 2.6, 0), pale(0.6, 4.5)));
  parts.push(paint(xf(new THREE.TorusGeometry(0.52, 0.06, 4, 16).rotateX(Math.PI / 2), [0, 2.4, 0]), 0x9fe6ff, [1.3, 1.4]));
  parts.push(paint(xf(new THREE.TorusGeometry(0.47, 0.05, 4, 16).rotateX(Math.PI / 2), [0, 3.3, 0]), 0x9fe6ff, [1.3, 1.4]));
  parts.push(paint(xf(new THREE.OctahedronGeometry(0.38, 0).scale(1, 1.6, 1), [0, 4.95, 0]), 0x8fe0ff, [1.3, 1.45]));
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + 0.3;
    const h = 0.6 + rnd() * 0.5;
    parts.push(shadeGeo(box(0.4, h, 0.32).translate(Math.cos(a) * 1.95, 0.4 + h / 2, Math.sin(a) * 1.95), pale(0.4, 1.4)));
  }
  parts.push(mossCap(1.2, rnd, [0.25, 0.3, 0.25], 0.42));
  return combine(parts);
};

/** +spawn: a washing line between two posts, with a quilt, a shirt and socks pegged out (Granny Pell's garden). */
const washingLine: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  for (const sx of [-2.2, 2.2]) parts.push(paint(xf(cyl(0.06, 0.07, 2.1, 5), [sx, 0, 0]), 0x6b4d30, [0.75, 1]));
  parts.push(paint(xf(new THREE.CylinderGeometry(0.015, 0.015, 4.4, 4).rotateZ(Math.PI / 2), [0, 2.0, 0]), 0xe8e2d0, [1, 1]));
  const cloth: [number, number, number, number][] = [
    [-1.5, 0.8, 0.9, 0xd9605a],
    [-0.3, 0.6, 0.7, 0xf2e6c8],
    [0.6, 0.35, 0.45, 0x6aa0d8],
    [1.2, 0.35, 0.45, 0xe0b040],
    [1.75, 0.3, 0.4, 0x9a6ad0],
  ];
  for (const [x, w, h, c] of cloth) parts.push(paint(xf(box(w, h, 0.03), [x, 2.0 - h, 0], [(rnd() - 0.5) * 0.15, 0, (rnd() - 0.5) * 0.08]), c, [0.85, 1.1]));
  return combine(parts);
};

/** An ore cart on rails: a riveted tub on wheels, heaped with ore and a few glints. */
const oreCart: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  parts.push(paint(xf(new THREE.CylinderGeometry(0.75, 0.6, 0.85, 8).scale(1, 1, 1.35), [0, 0.75, 0]), 0x5a4a3c, [0.65, 1]));
  parts.push(paint(xf(new THREE.TorusGeometry(0.73, 0.05, 4, 12).rotateX(Math.PI / 2).scale(1, 1, 1.35), [0, 1.16, 0]), 0x3a3a42, [1, 1]));
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) parts.push(paint(xf(new THREE.CylinderGeometry(0.28, 0.28, 0.12, 8).rotateZ(Math.PI / 2), [sx * 0.62, 0.28, sz * 0.62]), 0x2e2e34, [0.8, 1]));
  for (let i = 0; i < 6; i++) parts.push(paint(rockGeo(0.28 + rnd() * 0.12, rnd, 0.8).translate((rnd() - 0.5) * 0.6, 1.1, (rnd() - 0.5) * 0.8), i % 3 ? 0x6a6e78 : 0x8a7a5a, [0.8, 1.05]));
  parts.push(paint(xf(new THREE.OctahedronGeometry(0.14, 0), [0.2, 1.42, 0.1]), 0x8fe0ff, [1.3, 1.4]));
  return combine(parts);
};

/** A short run of mine rails (6 yd along +z) on sleepers. */
const mineRails: Gen = () => {
  const parts: THREE.BufferGeometry[] = [];
  for (const sx of [-0.45, 0.45]) parts.push(paint(xf(box(0.08, 0.07, 6.2), [sx, 0.04, 0]), 0x55555e, [1, 1.05]));
  for (let i = 0; i < 8; i++) parts.push(paint(xf(box(1.3, 0.06, 0.2), [0, 0.0, -2.8 + i * 0.8]), 0x5a3f2a, [0.8, 1]));
  return combine(parts);
};

/** A spoil heap: a mound of grey rubble and dirt spilled from the mine. */
const spoilHeap: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [paint(xf(blob(2.2, rnd, 0.25, 10, 6, 0.6), [0, 0.2, 0], [0, 0, 0], [1.3, 0.55, 1.0]), 0x7a7266, [0.6, 1.0])];
  for (let i = 0; i < 9; i++) {
    const a = rnd() * Math.PI * 2;
    const d = rnd() * 2.4;
    parts.push(paint(rockGeo(0.25 + rnd() * 0.3, rnd, 0.7).translate(Math.cos(a) * d * 1.2, 0.2 + (1 - d / 2.4) * 0.9, Math.sin(a) * d), i % 2 ? 0x6c707a : 0x878b94, [0.7, 1.05]));
  }
  return combine(parts);
};

/** Spare timbers: a lean-to of beams and a stack of props by the mine. */
const mineBeams: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 4; i++) parts.push(paint(xf(box(0.3, 3.2, 0.3), [-1 + i * 0.6, 0, 0], [0.32, 0, (rnd() - 0.5) * 0.1]), i % 2 ? 0x5a3f2a : 0x6b4d30, [0.6, 1]));
  for (let i = 0; i < 5; i++) parts.push(paint(xf(box(2.6, 0.26, 0.26), [0.4, 0.13 + (i % 3) * 0.27, 1.4 + Math.floor(i / 3) * 0.3]), 0x6b4d30, [0.7, 1]));
  parts.push(paint(xf(box(0.22, 0.32, 0.22), [-0.8, 2.5, 0.7]), 0xffd68a, [1.3, 1.4]));
  return combine(parts);
};

/** Scree: a spill of small grey stones (walk-through detail). */
const scree: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  const n = 6 + Math.floor(rnd() * 6);
  for (let i = 0; i < n; i++) {
    const a = rnd() * Math.PI * 2;
    const d = Math.sqrt(rnd()) * 0.9;
    parts.push(paint(rockGeo(0.1 + rnd() * 0.16, rnd, 0.55).translate(Math.cos(a) * d, 0, Math.sin(a) * d), i % 3 ? 0x7c8494 : 0x98a0aa, [0.75, 1.05]));
  }
  return combine(parts);
};

registerOutdoorProcs({
  ore_cart: oreCart,
  mine_rails: mineRails,
  spoil_heap: spoilHeap,
  mine_beams: mineBeams,
  scree,
  reeds,
  dock,
  mine_mouth: mineMouth,
  cave_mouth: caveMouth,
  lore_stone: loreStone,
  waystone,
  washing_line: washingLine,
  slime_puddle: slimePuddle,
  barley,
});

registerOutdoorProcs({
  spruce: conifer(12.5, 7, 0.27),
  fir: conifer(8.5, 5, 0.33),
  spruce_young: conifer(4.6, 4, 0.34, 6),
  cliff_spire: cliff(2.6, [1.0, 2.2, 0.9], 0.78, 0.35),
  cliff_shelf: cliff(3.4, [1.45, 0.95, 1.15], 0.68, 0.8),
  cliff_block: cliff(2.4, [1.1, 1.5, 1.0], 0.72, 0.55),
  mossrock: mossRock(0.85),
  mossrock_big: mossRock(1.45),
  column_pale: columnPale,
  ruin_arch: ruinArch,
  ruin_colonnade: ruinColonnade,
  ruin_wall_pale: ruinWallPale,
  ruin_tower: ruinTower,
  glow_monument: glowMonument,
  glow_crystals: glowCrystals,
  barrow_ruin: barrowRuin,
  flagstone,
  bluebells: bells([0x4f74e8, 0x6a5ae0, 0x86a8ff, 0x7d4fd0], VAL.leaf),
  violets: bells([0x8a5ae8, 0xa98af0, 0x5f6ae0, 0xe8e8ff], VAL.leaf),
  fern,
  shrub,
  portal_ring_pale: portalRingPale,
});

/** Low-poly conifer for the instanced far ring (3 jagged tiers). */
export function farSpruceGeometry(leaf: number): THREE.BufferGeometry {
  let seed = 7;
  const rnd = (): number => {
    seed = (seed * 16807) % 2147483647;
    return seed / 2147483647;
  };
  const c = rgb(leaf);
  const parts: THREE.BufferGeometry[] = [paint(cyl(0.2, 0.32, 2.4, 5), VAL.bark, [0.5, 0.9])];
  for (let i = 0; i < 3; i++) {
    const t = i / 2;
    const r = 2.6 * (1 - t * 0.6);
    const h = 4.2 - t * 1.0;
    const tier = jaggedTier(r, h, 5, rnd).translate(0, 1.6 + t * 5.4 + h / 2, 0);
    const k = 0.8 + t * 0.35;
    parts.push(shadeGeo(tier, (_x, y) => [c[0] * k * (0.8 + 0.06 * (y - 1.6 - t * 5.4)), c[1] * k * (0.8 + 0.06 * (y - 1.6 - t * 5.4)), c[2] * k]));
  }
  return combine(parts);
}
